import { useEffect, useState } from "react";
import axios from "axios";
import AdminLayout from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { CheckCircle2, XCircle, RefreshCw, Database, Cpu, Globe2, Mail, FileText, Users, Activity } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminGlobalSettings = () => {
  const { token } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/platform-stats`, auth);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AdminLayout title="Global Settings">
      <div className="space-y-6" data-testid="admin-global-settings">
        <div className="flex justify-end">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-platform-stats">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Platform stats */}
        <Card>
          <CardHeader><CardTitle>Platform overview</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Stat icon={<Globe2 className="h-5 w-5" />} label="Active sites" value={data?.projects ?? 0} />
              <Stat icon={<FileText className="h-5 w-5" />} label="Total articles" value={data?.total_articles ?? 0} />
              <Stat icon={<FileText className="h-5 w-5" />} label="Published" value={data?.published_articles ?? 0} />
              <Stat icon={<Users className="h-5 w-5" />} label="Users" value={data?.users ?? 0} />
              <Stat icon={<Activity className="h-5 w-5" />} label="Events" value={data?.analytics_events ?? 0} />
            </div>
          </CardContent>
        </Card>

        {/* Integration health */}
        <Card>
          <CardHeader><CardTitle>Integration health</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              <IntegrationRow icon={<Cpu className="h-5 w-5" />} name="B2B Hub AI (paraphrase)" health={data?.integrations?.b2bhub} />
              <IntegrationRow icon={<Globe2 className="h-5 w-5" />} name="ip-api.com (analytics geo)" health={data?.integrations?.ip_api} />
              <IntegrationRow icon={<Database className="h-5 w-5" />} name="NewsData.io (feed import)" health={data?.integrations?.newsdata} />
            </div>
          </CardContent>
        </Card>

        {/* Environment (read-only) */}
        <Card>
          <CardHeader><CardTitle>Environment</CardTitle></CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <EnvRow icon={<Mail className="h-4 w-4" />} label="Admin email" value={data?.env?.admin_email} />
              <EnvRow icon={<Globe2 className="h-4 w-4" />} label="Default project (fallback)" value={data?.env?.default_project_slug} />
              <EnvRow icon={<Activity className="h-4 w-4" />} label="CORS origins" value={data?.env?.cors_origins} mono />
            </dl>
            <p className="mt-4 text-xs text-slate-500">
              Environment variables are set on Railway (backend) and Netlify (frontend). Edit them in those dashboards — they are not configurable from this UI for security.
            </p>
          </CardContent>
        </Card>

        {/* Per-domain SEO endpoints quick links */}
        <Card>
          <CardHeader><CardTitle>SEO endpoints (per-domain)</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-3">
              Each news domain serves its own dynamic <code className="bg-slate-100 px-1 rounded">sitemap.xml</code>, <code className="bg-slate-100 px-1 rounded">robots.txt</code>, and <code className="bg-slate-100 px-1 rounded">llms.txt</code> via the backend. Submit each domain's sitemap to Google Search Console + Bing Webmaster Tools.
            </p>
            <ul className="text-sm space-y-1 text-slate-700 list-disc list-inside">
              <li>Welcomed AI crawlers in robots.txt: GPTBot, ChatGPT-User, ClaudeBot, anthropic-ai, PerplexityBot, Google-Extended, CCBot</li>
              <li>llms.txt index — improves citations from AI assistants (ChatGPT, Claude, Gemini, Perplexity)</li>
              <li>News article pages emit `NewsArticle` + `BreadcrumbList` JSON-LD automatically</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

const Stat = ({ icon, label, value }) => (
  <div className="rounded-lg border bg-slate-50 p-4">
    <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider mb-1">{icon}<span>{label}</span></div>
    <p className="text-2xl font-bold text-slate-900">{Number(value).toLocaleString()}</p>
  </div>
);

const IntegrationRow = ({ icon, name, health }) => {
  const ok = !!health?.ok;
  return (
    <div className="flex items-center justify-between p-3 rounded-lg border" data-testid={`integration-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center gap-3">
        <div className="text-slate-400">{icon}</div>
        <div>
          <p className="font-medium">{name}</p>
          {!ok && health?.error && <p className="text-xs text-amber-700 mt-0.5">{health.error}</p>}
          {ok && health?.status && <p className="text-xs text-slate-500 mt-0.5">HTTP {health.status}</p>}
        </div>
      </div>
      {ok ? (
        <Badge className="bg-emerald-100 text-emerald-700 gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> Connected</Badge>
      ) : (
        <Badge variant="outline" className="text-amber-700 border-amber-200 gap-1.5"><XCircle className="h-3.5 w-3.5" /> Down</Badge>
      )}
    </div>
  );
};

const EnvRow = ({ icon, label, value, mono }) => (
  <div className="flex flex-col gap-1">
    <dt className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500">{icon}{label}</dt>
    <dd className={`text-slate-900 ${mono ? "font-mono text-xs break-all" : "font-medium"}`}>{value || <span className="text-slate-400">—</span>}</dd>
  </div>
);

export default AdminGlobalSettings;
